import { AlertTriangle, X } from 'lucide-react';
import React, { useState } from 'react';

import { MonacoJsonEditor } from '../MonacoJsonEditor';
import styles from './schema-editor.module.css';

interface SchemaJsonDialogProps {
  schemaName: string;
  /** The field that uniquely identifies a record; warned about when absent. */
  externalIdField: string;
  /** The draft schema, pretty-printed. */
  value: string;
  onApply: (schema: Record<string, unknown>) => void;
  onClose: () => void;
}

interface ParsedSchema {
  schema?: Record<string, unknown>;
  /** Set only for JSON that parses but is not a schema — Monaco reports syntax errors itself. */
  structureError?: string;
}

function parseSchema(json: string): ParsedSchema {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return {};
  }

  const isObject = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed);
  if (!isObject) {
    return { structureError: 'A JSON Schema must be an object.' };
  }

  const schema = parsed as Record<string, unknown>;
  const properties = schema.properties;
  const hasPropertiesObject =
    typeof properties === 'object' && properties !== null && !Array.isArray(properties);
  if (!hasPropertiesObject) {
    return { structureError: 'A JSON Schema must have a "properties" object.' };
  }

  const definitions = Object.values(properties as Record<string, unknown>);
  const everyDefinitionIsObject = definitions.every(
    definition => typeof definition === 'object' && definition !== null && !Array.isArray(definition),
  );
  if (!everyDefinitionIsObject) {
    return { structureError: 'Every entry in "properties" must be an object.' };
  }

  return { schema };
}

/** Raw editor for an existing schema. Applying writes the draft, not the server. */
export const SchemaJsonDialog: React.FC<SchemaJsonDialogProps> = ({
  schemaName,
  externalIdField,
  value,
  onApply,
  onClose,
}) => {
  const [draft, setDraft] = useState(value);

  const parsed = parseSchema(draft);
  const properties = (parsed.schema?.properties ?? {}) as Record<string, unknown>;
  const missingExternalIdField = Boolean(
    parsed.schema && externalIdField && !(externalIdField in properties),
  );

  return (
    <div className={styles.jsonDialogOverlay}>
      <div
        className={styles.jsonDialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="schema-json-dialog-title">
        <header className={styles.jsonDialogHeader}>
          <div className={styles.jsonDialogTitleGroup}>
            <span className={styles.jsonDialogKicker}>JSON Schema</span>
            <h3 id="schema-json-dialog-title" className={styles.jsonDialogTitle}>
              {schemaName}
            </h3>
          </div>
          <button
            type="button"
            className={styles.jsonDialogCloseButton}
            aria-label="Close JSON editor"
            onClick={onClose}>
            <X size={18} />
          </button>
        </header>

        <div className={styles.jsonDialogBody}>
          <p className={styles.jsonDialogHelp}>
            Edit the raw schema for anything the attribute editor cannot express. Applying updates your
            draft — nothing is saved until you save the schema.
          </p>

          <MonacoJsonEditor value={draft} onChange={setDraft} height="440px" />

          {missingExternalIdField && (
            <p className={styles.jsonDialogWarning}>
              <AlertTriangle size={14} />
              The external ID field <code>{externalIdField}</code> is not one of these properties.
              Records cannot be identified without it.
            </p>
          )}

          {parsed.structureError && <p className={styles.jsonDialogError}>{parsed.structureError}</p>}
        </div>

        <footer className={styles.jsonDialogActions}>
          <button type="button" className={styles.jsonDialogCancelButton} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.jsonDialogApplyButton}
            onClick={() => parsed.schema && onApply(parsed.schema)}
            disabled={!parsed.schema}>
            Apply to draft
          </button>
        </footer>
      </div>
    </div>
  );
};
